import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Fuse from 'fuse.js';
import lodashCompact from 'lodash/fp/compact';

import {
  getListingCityStateString,
} from 'common/listingHelpers';

import log from 'client/lib/log';
import preventDefaultPropagation from 'client/helpers/preventDefaultPropagation';
import loadFuzzySearchListingsAction from 'client/actions/Listing/loadFuzzySearchListings';
import branch from 'client/core/branch';

import SrLink from 'client/components/SrLink/SrLink';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';

const debug = log.debug('sunroom:components:SrListingSearchWidget');

const FUZZY_SEARCH_CHAR_DELAY_MS = 80;

@branch({
  fuzzySearchListings: ['fuzzySearchListings'],
})
class SrListingSearchWidget extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    fuzzySearchListings: PropTypes.array.isRequired,
  }

  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      searchInput: '',
      listings: [],
    };

    this.fuzzy = {
      fuse: null,
      listingIdToListingMapping: {},
      mlsNumberToListingMapping: {},
    };
  }

  componentDidMount() {
    const { dispatch } = this.props;

    if (global.document) {
      global.document.addEventListener('keydown', (e) => {
        if (e.keyCode === 83 && (navigator.platform.match('Mac') ? e.metaKey : e.ctrlKey)) {
          e.preventDefault();
          dispatch(loadFuzzySearchListingsAction);
          this.setState({
            isOpen: !this.state.isOpen,
          });
          if (this._searchInputEl) {
            this._searchInputEl.focus();
          }
        }
      }, false);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      this.props.fuzzySearchListings.length === 0
      && nextProps.fuzzySearchListings.length > 0
    ) {
      const fuseOptions = {
        isCaseSensitive: false,
        includeScore: false,
        shouldSort: true,
        includeMatches: false,
        findAllMatches: false,
        minMatchCharLength: 1,
        location: 0,
        threshold: 0.5,
        distance: 50,
        useExtendedSearch: false,
        ignoreLocation: true,
        ignoreFieldNorm: false,
        keys: [
          'addressStreetDisplay',
        ],
      };

      const fuseIndex = Fuse.createIndex(fuseOptions.keys, nextProps.fuzzySearchListings);
      this.fuzzy.fuse = new Fuse(nextProps.fuzzySearchListings, fuseOptions, fuseIndex);
      this.fuzzy.listingIdToListingMapping = nextProps.fuzzySearchListings
        .reduce((memo, listing) => ({
          ...memo,
          [listing.id]: listing,
        }));
      this.fuzzy.mlsNumberToListingMapping = nextProps.fuzzySearchListings
        .reduce((memo, listing) => ({
          ...memo,
          [listing.mlsNumber]: listing,
        }));
    }
  }

  _onInputChange = (e) => {
    if (!process.browser) {
      return;
    }
    const input = e.target.value || '';

    this.setState({
      searchInput: input,
    });

    clearTimeout(this.timeout);
    this.timeout = setTimeout(this._runFuseSearch, FUZZY_SEARCH_CHAR_DELAY_MS);
  }

  _runFuseSearch = () => {
    const { searchInput } = this.state;

    if (searchInput.length >= 1 && this.fuzzy.fuse) {
      const mlsOrIdMatches = lodashCompact([
        this.fuzzy.listingIdToListingMapping[searchInput],
        this.fuzzy.mlsNumberToListingMapping[searchInput],
      ]);
      const fuseSearchResults = this.fuzzy.fuse.search(searchInput);

      debug({
        mlsOrIdMatches,
        fuseSearchResults,
      });

      const listings = mlsOrIdMatches.length > 0
        ? mlsOrIdMatches
        : (
          fuseSearchResults
            .map(({ item }) => item)
            .slice(0, 30)
        );

      this.setState({
        listings,
      });
    }
    else {
      this.setState({
        listings: [],
      });
    }
  }

  _closeSearch = () => {
    this.setState({
      isOpen: false,
    });
  }

  render() {
    const { isOpen, searchInput, listings } = this.state;
    if (!isOpen) {
      return null;
    }
    return (
      <div onClick={this._closeSearch} className='SrCenteredWidgetContainer'>
        <div onClick={preventDefaultPropagation} className='SrListingSearchWidget'>
          <div className='header'>
            <input
              ref={(el) => {
                this._searchInputEl = el;
              }}
              value={searchInput}
              type='text'
              onChange={this._onInputChange}
              placeholder='Sunlight search'
            />
          </div>
          {listings.length > 0 && (
            <div className='content'>
              <div className='contentGroupHeading'>
                <SrText
                  font='whitney-sc'
                >
                  sunroom listings
                </SrText>
              </div>
              {listings.map((listing) => (
                <SrLink
                  className='resultRowContainer listing'
                  onClick={this._closeSearch}
                  to={`/listing/${listing.id}`}
                >
                  <SrFlex className='resultRow' key={listing.id}>
                    <div className='resultName listing'>
                      <SrText
                        className='addressStreet'
                      >
                        {`${listing.addressStreetDisplay}`}
                      </SrText>
                      <SrText
                        className='addressCityState'
                      >
                        {` ${getListingCityStateString(listing)}`}
                      </SrText>
                    </div>
                    <div className='resultInfo'>
                      <SrFlex>
                        <SrText className='infoCell'>
                          {listing.status}
                        </SrText>
                        <SrText className='infoCell'>
                          {
                            listing.listingManagementCompany
                              ? listing.listingManagementCompany
                              : 'RealSavvy'
                          }
                        </SrText>
                        <SrText className='infoCell'>
                          {`MLS# ${listing.mlsNumber || '--'}`}
                        </SrText>
                        <SrText className='infoCell link'>
                          <SrLink to={`/admin/listings/edit/${listing.id}`}>
                            Edit
                          </SrLink>
                        </SrText>
                      </SrFlex>
                    </div>
                  </SrFlex>
                </SrLink>
              ))}
            </div>
          )}
          <SrLink
            onClick={this._closeSearch}
            className='footer'
            to='/admin/listings'
          >
            View all Sunroom listings →
          </SrLink>
        </div>
      </div>
    );
  }
}

SrListingSearchWidget.displayName = 'SrListingSearchWidget';

export default SrListingSearchWidget;
